import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import DataObjectIcon from '@mui/icons-material/DataObject';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FlagIcon from '@mui/icons-material/Flag';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PpaMinGeneration from './PpaMinGeneration';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import AssetChip from './AssetChip';
import ProcessingTimeline from './ProcessingTimeline';
import ContractStatusChip from './ContractStatusChip';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TaskTeamPicker from './TaskTeamPicker';
import TaskRecurrence from './TaskRecurrence';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

// Mirrors recurrence.describe() on the server. Duplicated deliberately and
// kept trivial: the alternative is a round-trip purely to render a label.
const CADENCE_LABEL = {
  monthly: 'Monthly', quarterly: 'Quarterly', annual: 'Annually',
  weekly: 'Weekly', daily: 'Daily', ad_hoc: 'Ad hoc', continuous: 'Continuous',
};
// Mirrors recurrence.describe_trigger() on the server.
const describeTrigger = (condition, offsetDays, offsetUnit) => {
  const cond = (condition || '').trim() || 'a trigger event';
  if (!offsetDays) return `On ${cond}`;
  const plural = offsetDays === 1 ? '' : 's';
  const unit = { business_days: `business day${plural}`, calendar_days: `day${plural}`,
                 months: `month${plural}` }[offsetUnit] || `day${plural}`;
  return `Within ${offsetDays} ${unit} of ${cond}`;
};

const describeRecurrence = (cadence, anchorKind, anchorDay) => {
  const cad = (cadence || '').toLowerCase();
  const label = CADENCE_LABEL[cad] || (cad ? cad : 'Unscheduled');
  if (!['monthly', 'quarterly', 'annual'].includes(cad) || !anchorKind) return label;
  if (anchorKind === 'first') return `${label} on the 1st`;
  if (anchorKind === 'mid') return `${label} on the 15th`;
  if (anchorKind === 'last') return `${label} on the last day`;
  const n = Number(anchorDay || 1);
  const suffix = (11 <= n % 100 && n % 100 <= 13) ? 'th'
    : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th');
  return `${label} on the ${n}${suffix}`;
};

// Human-readable labels for PPA extraction fields
const PPA_LABELS = {
  // Counterparties & Project
  buyer: 'Buyer',
  seller: 'Seller',
  project_name: 'Project Name',
  // Term & Dates
  term_years: 'Term / Tenor (Years)',
  start_date: 'Start Date',
  end_date: 'End Date',
  // Pricing
  contract_price_per_mwh: 'PPA Rate (A$/MWh)',
  price_currency: 'Currency',
  price_type: 'Price Type',
  price_escalation_percent: 'Price Escalation (%)',
  price_escalation_mechanism: 'Escalation Mechanism',
  indexing_mechanism: 'Indexing Mechanism',
  // Volume & Capacity
  capacity_mw: 'Capacity (MW)',
  volume_mwh_annual: 'Annual Volume (MWh)',
  contract_profile: 'Contract Profile',
  // Operational & Technical
  technology_type: 'Technology Type',
  settlement_type: 'Settlement Type',
  offtake_structure: 'Offtake Structure',
  location_region: 'Location / Region',
  billing_period: 'Billing Period',
  // Curtailment
  curtailment_provisions: 'Curtailment Provisions',
  // Performance & Guarantees
  performance_guarantees: 'Performance Guarantees',
  performance_security: 'Performance Security (Seller)',
  // Credit & Payment
  buyer_credit_support: 'Buyer Credit Support',
  payment_terms: 'Payment Terms',
  // Default & Termination
  events_of_default: 'Events of Default',
  termination_payments: 'Termination Payments',
  termination_clauses_summary: 'Termination Clauses',
  // Other Legal
  force_majeure_summary: 'Force Majeure',
  additional_notes: 'Additional Notes',

  // ── Rich PPA fields (Phase-2 extraction) ─────────────────────────────────
  // These map to terms.ppa.* on the Firestore doc (or top-level dates for
  // target_cod / cod_sunset). Edits go through the backend's
  // LEGACY_FIELD_MAP_PPA translator which knows how to write to the nested
  // path. Read-only display values come from `doc.extraction_data` (LLM raw).
  buyers_share_pct:                       "Buyer's Share (fraction)",
  min_expected_generation_mwh_per_year:   'Min Expected Generation (MWh/yr)',
  reference_node:                         'Reference Node',
  curtailment_strike_aud_per_mwh:         'Curtailment Strike (A$/MWh)',
  loss_factor_source:                     'Loss Factor Source',
  loss_factor_fixed:                      'Loss Factor (fixed)',
  lgc_treatment:                          'LGC Treatment',
  lgc_price_aud_each:                     'LGC Price (A$/LGC)',
  target_cod:                             'Target COD',
  cod_sunset:                             'COD Sunset',
  delay_damages_per_day_aud:              'Delay Damages (A$/day)',
  delay_damages_cap_aud:                  'Delay Damages Cap (A$)',
};

// Human-readable labels for general extraction fields
const GENERAL_LABELS = {
  parties: 'Parties',
  contract_title: 'Contract Title',
  contract_type_detail: 'Contract Type',
  effective_date: 'Effective Date',
  expiry_date: 'Expiry Date',
  contract_value: 'Contract Value',
  currency: 'Currency',
  key_terms: 'Key Terms',
  obligations: 'Obligations',
  termination_clauses: 'Termination Clauses',
  governing_law: 'Governing Law',
  dispute_resolution: 'Dispute Resolution',
  confidentiality_provisions: 'Confidentiality',
  additional_notes: 'Additional Notes',
};

// Fields that should use monospace font for their values
const MONO_FIELDS = new Set([
  'capacity_mw', 'contract_price_per_mwh', 'price_escalation_percent',
  'term_years', 'volume_mwh_annual', 'contract_value',
  // Rich numeric fields render in mono too — easier to scan tables of numbers.
  'buyers_share_pct', 'min_expected_generation_mwh_per_year',
  'curtailment_strike_aud_per_mwh', 'loss_factor_fixed',
  'lgc_price_aud_each', 'delay_damages_per_day_aud', 'delay_damages_cap_aud',
]);

// Numeric fields that should be parsed as numbers
const NUMERIC_FIELDS = new Set([
  'capacity_mw', 'contract_price_per_mwh', 'price_escalation_percent',
  'term_years', 'volume_mwh_annual',
  // Rich numerics
  'buyers_share_pct', 'min_expected_generation_mwh_per_year',
  'curtailment_strike_aud_per_mwh', 'loss_factor_fixed',
  'lgc_price_aud_each', 'delay_damages_per_day_aud', 'delay_damages_cap_aud',
]);

// Fields that are arrays and need special handling
const ARRAY_FIELDS = new Set([
  'parties', 'key_terms', 'obligations', 'termination_clauses',
]);

// Long-text fields that should use multiline input
const MULTILINE_FIELDS = new Set([
  'force_majeure_summary', 'termination_clauses_summary', 'additional_notes',
  'confidentiality_provisions', 'dispute_resolution', 'price_escalation_mechanism',
  'indexing_mechanism', 'curtailment_provisions', 'performance_guarantees',
  'performance_security', 'buyer_credit_support', 'payment_terms',
  'events_of_default', 'termination_payments', 'contract_profile',
]);

// Date fields that should use date picker
const DATE_FIELDS = new Set([
  'start_date', 'end_date', 'effective_date', 'expiry_date',
  'target_cod', 'cod_sunset',
]);

// Convert any date string to YYYY-MM-DD for date picker input
const toDateInputValue = (val) => {
  if (!val) return null;
  try {
    const d = dayjs(val);
    return d.isValid() ? d : null;
  } catch {
    return null;
  }
};

const isProcessing = (status) =>
  ['uploading', 'processing_ocr', 'processing_classification', 'processing_extraction', 'extracting_tasks'].includes(status);

const PRIORITY_COLORS = {
  high: { bg: 'rgba(229, 56, 79, 0.08)', color: 'error.main', border: '1px solid rgba(229, 56, 79, 0.2)' },
  medium: { bg: 'rgba(212, 147, 13, 0.08)', color: '#d4930d', border: '1px solid rgba(212, 147, 13, 0.2)' },
  low: { bg: 'rgba(59, 130, 246, 0.08)', color: '#3b82f6', border: '1px solid rgba(59, 130, 246, 0.2)' },
};

const CATEGORY_COLORS = {
  billing: '#9b59b6',
  renewal: '#e67e22',
  compliance: '#2d7dd2',
  review: '#D98A2B',
  deadline: '#ef4444',
  other: '#a8b0bd',
};

const formatValue = (value) => {
  if (value === null || value === undefined) return '\u2014';
  if (Array.isArray(value)) {
    if (value.length === 0) return '\u2014';
    if (typeof value[0] === 'object' && value[0].party) {
      return value.map((v) => `${v.party}: ${v.obligation}`).join('\n');
    }
    return value.join(', ');
  }
  if (typeof value === 'number') {
    return value.toLocaleString();
  }
  return String(value);
};

const valueToEditString = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    if (typeof value[0] === 'object' && value[0]?.party) {
      return value.map((v) => `${v.party}: ${v.obligation}`).join('\n');
    }
    return value.join(', ');
  }
  return String(value);
};

const formatContractDate = (dateStr) => {
  if (!dateStr) return '\u2014';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const metaLabelSx = {
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '0.65rem',
  fontWeight: 600,
};

// ─── Adapter: new Firestore doc → old shape this component was written for.
// The contracts module recently moved off Postgres onto Firestore (typed
// sub-objects under `terms.ppa` / `terms.isda`, processing pipeline under
// `processing.status`, source metadata under `source.*`). This file is ~1400
// lines and references the old flat shape in dozens of places — rewriting all
// of those is a bigger lift than we want today, so we coerce the new doc into
// the old shape at the boundary and let the existing code keep working.
//
// When we revisit the detail-page UX (per-period price schedule editor,
// obligations register, etc.), this adapter goes away in favour of consuming
// the new shape directly.
// Pull the first YYYY-MM-DD out of a string. Falls back to the second arg
// when the first is unparseable. Used because the LLM sometimes returns
// descriptive text ("Commercial Operation Date (target: 2025-08-31)") in date
// fields. Returns ISO date string or null.
function toIsoDateOrNull(value, fallback = null) {
  if (!value) return fallback;
  if (typeof value !== 'string') return value;
  const m = value.match(/\d{4}-\d{2}-\d{2}/);
  if (m) return m[0];
  // Already a clean date string the JS Date can parse?
  const d = new Date(value);
  if (!Number.isNaN(d.valueOf())) return d.toISOString().slice(0, 10);
  return fallback;
}

function adaptContract(doc) {
  if (!doc) return null;
  // Already in old shape? (defensive — shouldn't happen post-migration)
  if (doc.id !== undefined && doc.extraction !== undefined) return doc;

  const ppa  = doc.terms?.ppa  || {};
  const isda = doc.terms?.isda || {};
  const extractionType = doc.contract_kind === 'PPA' ? 'ppa' : 'general';

  // LLM occasionally drops descriptive text into effective_date / termination_date
  // ("Commercial Operation Date (target: 2025-08-31)"). Coerce to a clean ISO
  // date, falling back to target_cod / cod_sunset when those are populated
  // because for construction-phase PPAs effective_date == COD == target_cod.
  const effectiveIso   = toIsoDateOrNull(doc.effective_date,   doc.target_cod);
  const terminationIso = toIsoDateOrNull(doc.termination_date, doc.cod_sunset);

  const extractionFields = doc.contract_kind === 'PPA' ? {
    extraction_type:           'ppa',
    buyer:                     doc.counterparty,
    seller:                    null,
    project_name:              doc.project?.name,
    capacity_mw:               doc.project?.capacity_mw ?? ppa.contracted_mw,
    contract_price_per_mwh:    ppa.strike_aud_per_mwh,
    price_currency:            'AUD',
    price_escalation_percent:  ppa.indexation_rate_pct != null ? ppa.indexation_rate_pct * 100 : null,
    price_escalation_mechanism: null,
    term_years:                null,
    start_date:                effectiveIso,
    end_date:                  terminationIso,
    settlement_type:           ppa.settlement_type,
    location_region:           ppa.region || doc.project?.region,
    volume_mwh_annual:         ppa.volume_cap_mwh_per_year,
    indexing_mechanism:        null,
    technology_type:           null,
    offtake_structure:         ppa.offtake,
    billing_period:            null,
    force_majeure_summary:     null,
    termination_clauses_summary: null,
    additional_notes:          null,
    // Rich PPA fields (these have homes in terms.ppa.* — surfaced here so
    // they appear in the edit table; edits flow back via the legacy field
    // map → field_path translation on the backend).
    buyers_share_pct:                       ppa.buyers_share_pct,
    min_expected_generation_mwh_per_year:   ppa.min_expected_generation_mwh_per_year,
    reference_node:                         ppa.reference_node,
    curtailment_strike_aud_per_mwh:         ppa.curtailment_strike_aud_per_mwh,
    loss_factor_source:                     ppa.loss_factor_source,
    loss_factor_fixed:                      ppa.loss_factor_fixed,
    lgc_treatment:                          ppa.lgc_treatment,
    lgc_price_aud_each:                     ppa.lgc_price_aud_each,
    target_cod:                             doc.target_cod,
    cod_sunset:                             doc.cod_sunset,
    delay_damages_per_day_aud:              ppa.delay_damages?.per_day_aud,
    delay_damages_cap_aud:                  ppa.delay_damages?.cap_aud,
  } : {
    extraction_type:           'general',
    parties:                   doc.counterparty ? [doc.counterparty] : [],
    contract_title:            doc.source?.filename,
    contract_type_detail:      doc.contract_kind,
    effective_date:            doc.effective_date,
    expiry_date:               doc.termination_date,
    contract_value:            null,
    currency:                  null,
    key_terms:                 [],
    obligations:               [],
    termination_clauses:       [],
    governing_law:             isda.governing_law,
    dispute_resolution:        isda.jurisdiction,
    confidentiality_provisions: null,
    additional_notes:          isda.notes,
  };

  const pdfRel = (doc.source?.pdf_uri || '').replace(/^gs:\/\//, '');
  return {
    ...doc,
    id:                doc.contract_id,
    original_filename: doc.source?.filename,
    uploaded_at:       doc.source?.uploaded_at,
    contract_type:     doc.contract_kind,
    // The old `status` field was the processing pipeline status. The new
    // shape splits processing-status from lifecycle-status; here we surface
    // processing.status but map 'ready' → 'completed' so the 1400-line
    // legacy component (which checks `status === 'completed'` to render the
    // extraction tabs) doesn't break. Without this map the body renders
    // blank — exactly the bug we hit on first upload.
    status: (() => {
      const s = doc.processing?.status;
      return s === 'ready' ? 'completed' : s;
    })(),
    lifecycle_status:  doc.status,
    error_message:    doc.processing?.error,
    start_date:        effectiveIso,
    end_date:          terminationIso,
    gcs_pdf_path:      pdfRel,
    gcs_markdown_path: pdfRel ? pdfRel.replace('.pdf', '.md') : null,
    // The legacy renderer reads field values from
    // `contract.extraction.extracted_data[key]` (via PPA_LABELS / GENERAL_LABELS).
    // We have TWO sources of truth:
    //   1. doc.extraction_data — the raw LLM output, merged in by the backend
    //      (GET endpoint includes latest extraction subdoc). This has ALL the
    //      descriptive fields the legacy view expects (force_majeure_summary,
    //      payment_terms, curtailment_provisions, etc.).
    //   2. extractionFields — derived from the new Firestore PpaTerms shape.
    //      Used as fallback for fields not in the raw extraction.
    // Merge so anything explicitly extracted by the LLM wins.
    extraction: {
      extraction_type: extractionFields.extraction_type,
      extracted_data:  { ...extractionFields, ...(doc.extraction_data || {}) },
      ...extractionFields,
      ...(doc.extraction_data || {}),
    },
  };
}

const ContractDetail = ({ contractId, onBack, onDelete }) => {
  const [contract, setContract] = useState(null);
  const [markdown, setMarkdown] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markdownLoading, setMarkdownLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [activating, setActivating] = useState(false);

  // Global edit mode
  const [editMode, setEditMode] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Tasks state
  const [tasks, setTasks] = useState([]);
  // Teams for the assignment dropdown. Org-wide and small, so fetched once
  // per mount rather than per task row.
  const [teams, setTeams] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  // Actions menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = Boolean(menuAnchor);

  // Track original values to detect changes
  const originalDataRef = useRef({});

  const fetchContract = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/contracts/${contractId}`
      );
      setContract(adaptContract(response.data));
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load contract');
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  useEffect(() => {
    if (!contract || !isProcessing(contract.status)) return;
    const interval = setInterval(fetchContract, 3000);
    return () => clearInterval(interval);
  }, [contract, fetchContract]);

  const fetchMarkdown = useCallback(async () => {
    if (markdown || !contract?.gcs_markdown_path) return;
    setMarkdownLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/contracts/${contractId}/markdown`
      );
      setMarkdown(response.data.markdown);
    } catch (err) {
      setMarkdown('Failed to load markdown content.');
    } finally {
      setMarkdownLoading(false);
    }
  }, [contractId, contract, markdown]);

  // Tasks now come straight from the contract doc — `obligations` (recurring)
  // and `events` (one-off dated). The legacy /api/contracts/<id>/tasks
  // endpoint was removed when we migrated to Firestore. Each derived task
  // carries an `_idx` + `_kind` so edit/delete handlers can address the
  // right element in the parent array.
  useEffect(() => {
    if (!contract) { setTasks([]); return; }
    // Team ownership rides along on the contract payload as `task_teams`,
    // keyed by the same synthetic task id we build below. It lives in
    // Postgres rather than on the obligation, so it survives reprocessing.
    const owners = contract.task_teams || {};
    const teamFor = (id) => ({
      team_id:        owners[id]?.team_id ?? null,
      team_name:      owners[id]?.team_name ?? null,
      team_is_manual: owners[id]?.is_manual ?? false,
    });
    // Recurrence overrides live in Postgres so a reprocess can't wipe them;
    // they arrive on the contract payload keyed by the same task id.
    const sched = contract.task_schedule || {};
    const schedFor = (id, raw) => {
      const o = sched[id];
      const cad = o?.cadence || raw?.cadence;
      // The user's override wins; then extraction's own classification (stored
      // on the obligation); then date-based, which is what everything
      // effectively was before this flag existed.
      const timing = o?.timing_type || raw?.timing_type || 'date';
      const cond   = o?.trigger_condition ?? raw?.trigger_condition ?? null;
      const offDays = o?.trigger_offset_days ?? raw?.trigger_offset_days ?? null;
      const offUnit = o?.trigger_offset_unit ?? raw?.trigger_offset_unit ?? null;
      return {
        recurrence_pattern:   cad,
        anchor_kind:          o?.anchor_kind ?? null,
        anchor_day:           o?.anchor_day ?? null,
        recurrence_is_manual: Boolean(o),
        timing_type:          timing,
        trigger_condition:    cond,
        trigger_offset_days:  offDays,
        trigger_offset_unit:  offUnit,
        recurrence_label:     timing === 'trigger'
          ? describeTrigger(cond, offDays, offUnit)
          : describeRecurrence(cad, o?.anchor_kind, o?.anchor_day),
      };
    };
    const rows = [];
    (contract.obligations || []).forEach((o, idx) => {
      rows.push({
        id:                 `obligation:${o.code || idx}`,
        _kind:              'obligation',
        _idx:               idx,
        _code:              o.code,
        ...teamFor(`obligation:${o.code || idx}`),
        title:              o.label || o.code || 'Untitled',
        description:        o.description || o.notes,
        due_date:           o.next_due || null,    // null for daily/continuous
        status:             o.status === 'met' ? 'completed' : 'pending',
        priority:           'medium',
        category:           'compliance',
        is_recurring:       true,
        clause_ref:         o.clause_ref,
        ...schedFor(`obligation:${o.code || idx}`, o),
      });
    });
    (contract.events || []).forEach((e, idx) => {
      rows.push({
        id:                 `event:${e.code || idx}`,
        _kind:              'event',
        _idx:               idx,
        _code:              e.code,
        ...teamFor(`event:${e.code || idx}`),
        title:              e.label || e.code || 'Untitled',
        description:        e.notes,
        due_date:           e.due_date || null,
        status:             e.status === 'met' ? 'completed' : 'pending',
        priority:           e.consequence ? 'high' : 'medium',
        category:           'deadline',
        is_recurring:       false,
        clause_ref:         e.clause_ref,
      });
    });
    // Sort: dated items first (by date asc), undated last
    rows.sort((a, b) => {
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return a.due_date.localeCompare(b.due_date);
    });
    setTasks(rows);
  }, [contract]);

  useEffect(() => {
    if (activeTab === 2) {
      fetchMarkdown();
    }
  }, [activeTab, fetchMarkdown]);

  // Teams for the Assigned-to dropdown. Failure is non-fatal — the picker
  // just shows the already-assigned name with no alternatives to switch to.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE_URL}/api/teams`)
      .then((r) => { if (!cancelled) setTeams(r.data.teams || []); })
      .catch(() => { if (!cancelled) setTeams([]); });
    return () => { cancelled = true; };
  }, []);

  // Patch one task's team in place after the picker saves, so the row updates
  // without refetching the whole contract.
  const handleTeamChanged = (taskId, assignment) => {
    setTasks((prev) => prev.map((t) => (t.id === taskId ? {
      ...t,
      team_id:        assignment.team_id ?? null,
      team_name:      assignment.team_name ?? null,
      team_is_manual: assignment.is_manual ?? false,
    } : t)));
  };

  // Patch one task's recurrence in place after the editor saves.
  const handleRecurrenceChanged = (taskId, res) => {
    setTasks((prev) => prev.map((t) => (t.id === taskId ? {
      ...t,
      recurrence_pattern:   res.cadence ?? t.recurrence_pattern,
      anchor_kind:          res.anchor_kind ?? null,
      anchor_day:           res.anchor_day ?? null,
      recurrence_is_manual: res.recurrence_is_manual ?? true,
      recurrence_label:     res.recurrence_label ?? t.recurrence_label,
    } : t)));
  };

  const [autoAssigning, setAutoAssigning] = useState(false);
  const unassignedCount = tasks.filter((t) => !t.team_id).length;

  // Re-run routing over the tasks already on the contract. Used for documents
  // processed before teams existed, and after editing team descriptions.
  const handleAutoAssign = async () => {
    setAutoAssigning(true);
    try {
      const r = await axios.post(
        `${API_BASE_URL}/api/contracts/${contractId}/tasks/auto-assign`,
      );
      const owners = r.data.task_teams || {};
      setTasks((prev) => prev.map((t) => ({
        ...t,
        team_id:        owners[t.id]?.team_id ?? null,
        team_name:      owners[t.id]?.team_name ?? null,
        team_is_manual: owners[t.id]?.is_manual ?? false,
      })));
    } catch (e) {
      // Non-fatal — the rows just stay as they are.
      console.warn('Auto-assign failed', e?.response?.data || e);
    } finally {
      setAutoAssigning(false);
    }
  };

  // Helper: write back a modified obligations or events array via the parent
  // PATCH endpoint. Uses the {fields: {...}} legacy body which we already
  // translate to terms-paths on the backend; both 'obligations' and 'events'
  // are in EDITABLE_ROOTS so this lands on Firestore through apply_edit.
  const patchArray = async (kind, nextArray) => {
    const key = kind === 'obligation' ? 'obligations' : 'events';
    const res = await axios.patch(
      `${API_BASE_URL}/api/contracts/${contractId}`,
      { field_path: key, value: nextArray }
    );
    setContract(adaptContract(res.data));
  };

  const handleToggleTaskStatus = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    try {
      if (task._kind === 'obligation' && task.status !== 'completed') {
        // Use the dedicated fulfill endpoint — it stamps last_fulfilled +
        // status='met' atomically and is audited differently from edits.
        const res = await axios.post(
          `${API_BASE_URL}/api/contracts/${contractId}/obligations/${task._code}/fulfill`,
          {}
        );
        setContract(adaptContract(res.data));
      } else {
        // Re-opening an obligation, or toggling an event status — write
        // the array directly.
        const key = task._kind === 'obligation' ? 'obligations' : 'events';
        const arr = [...(contract[key] || [])];
        const next = task.status === 'completed' ? 'upcoming' : 'met';
        arr[task._idx] = { ...arr[task._idx], status: next,
                           ...(task._kind === 'obligation' && next === 'met'
                               ? { last_fulfilled: new Date().toISOString().slice(0, 10) }
                               : {}) };
        await patchArray(task._kind, arr);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update task status');
    }
  };

  const handleAssignTaskDate = async (taskId, newDate) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    try {
      const dateStr = newDate ? newDate.format('YYYY-MM-DD') : null;
      const key = task._kind === 'obligation' ? 'obligations' : 'events';
      const arr = [...(contract[key] || [])];
      const field = task._kind === 'obligation' ? 'next_due' : 'due_date';
      arr[task._idx] = { ...arr[task._idx], [field]: dateStr };
      await patchArray(task._kind, arr);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign date');
    }
  };

  const handleEditTaskContent = async (taskId, patch) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    try {
      const key = task._kind === 'obligation' ? 'obligations' : 'events';
      const arr = [...(contract[key] || [])];
      // patch is {label?, description?, notes?} — map to the canonical field
      const updates = {};
      if (patch.title       != null) updates.label = patch.title;
      if (patch.description != null) {
        if (task._kind === 'obligation') updates.description = patch.description;
        else                              updates.notes       = patch.description;
      }
      arr[task._idx] = { ...arr[task._idx], ...updates };
      await patchArray(task._kind, arr);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    try {
      const key = task._kind === 'obligation' ? 'obligations' : 'events';
      const arr = (contract[key] || []).filter((_, idx) => idx !== task._idx);
      await patchArray(task._kind, arr);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete task');
    }
  };

  const handleReprocess = async () => {
    setMenuAnchor(null);
    setReprocessing(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/contracts/${contractId}/reprocess`
      );
      setMarkdown(null);
      await fetchContract();
    } catch (err) {
      setError('Failed to start reprocessing');
    } finally {
      setReprocessing(false);
    }
  };

  const handleToggleActive = async () => {
    setMenuAnchor(null);
    setActivating(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/contracts/${contractId}/activate`,
        contract.active ? { active: false } : {},
      );
      setContract(adaptContract(res.data));
    } catch (err) {
      setError('Failed to update active state');
    } finally {
      setActivating(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/api/contracts/${contractId}`);
      setDeleteDialogOpen(false);
      onDelete(contractId);
      onBack();
    } catch (err) {
      setError('Failed to delete contract');
      setDeleteDialogOpen(false);
    }
  };

  // --- Global Edit Mode ---
  const enterEditMode = () => {
    const extractedData = contract?.extraction?.extracted_data || {};
    const labels = contract?.extraction?.extraction_type === 'ppa' ? PPA_LABELS : GENERAL_LABELS;
    const initial = { _filename: contract?.original_filename || '' };
    Object.keys(labels).forEach((key) => {
      initial[key] = valueToEditString(extractedData[key]);
    });
    // Price schedule — an array of rows, edited as a whole. Deep-copy so
    // edits don't mutate the contract state until Save fires.
    const sched = contract?.terms?.ppa?.price_schedule || [];
    initial._price_schedule = JSON.parse(JSON.stringify(sched));
    setEditValues(initial);
    originalDataRef.current = { ...initial };
    setEditMode(true);
  };

  // Update one cell in the price-schedule array. `field` is 'price_aud_per_mwh' | 'period_start' | 'period_end' | 'kind'.
  const handlePriceScheduleChange = (rowIdx, field, value) => {
    setEditValues((prev) => {
      const next = (prev._price_schedule || []).slice();
      next[rowIdx] = { ...next[rowIdx], [field]: value };
      return { ...prev, _price_schedule: next };
    });
  };

  const cancelEditMode = () => {
    setEditMode(false);
    setEditValues({});
  };

  const handleFieldChange = (key, value) => {
    setEditValues((prev) => ({ ...prev, [key]: value }));
  };

  const saveAllFields = async () => {
    setSaving(true);
    try {
      // Determine which fields actually changed
      const changedFields = {};
      const labels = contract?.extraction?.extraction_type === 'ppa' ? PPA_LABELS : GENERAL_LABELS;

      Object.keys(labels).forEach((key) => {
        const newVal = (editValues[key] || '').trim();
        const origVal = (originalDataRef.current[key] || '').trim();
        if (newVal !== origVal) {
          let parsedValue = newVal;

          // Parse numeric fields
          if (NUMERIC_FIELDS.has(key) && parsedValue !== '') {
            const num = parseFloat(parsedValue);
            if (!isNaN(num)) parsedValue = num;
          }

          // Parse array fields (comma-separated)
          if (ARRAY_FIELDS.has(key) && typeof parsedValue === 'string' && key !== 'obligations') {
            parsedValue = parsedValue
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
          }

          // Empty → null
          if (parsedValue === '' || parsedValue === '\u2014') {
            parsedValue = null;
          }

          changedFields[key] = parsedValue;
        }
      });

      // Check if filename changed
      const newFilename = (editValues._filename || '').trim();
      const origFilename = (originalDataRef.current._filename || '').trim();
      const filenameChanged = newFilename !== origFilename && newFilename !== '';

      // Check if price schedule changed (deep compare on the whole array)
      const newSched = editValues._price_schedule || [];
      const origSched = originalDataRef.current._price_schedule || [];
      const scheduleChanged =
        JSON.stringify(newSched) !== JSON.stringify(origSched);
      if (scheduleChanged) {
        // Coerce price + dates to clean types before sending. Numeric coercion
        // is critical — the form puts strings in there.
        changedFields.price_schedule = newSched.map((r) => ({
          period_start:       r.period_start || null,
          period_end:         r.period_end   || null,
          price_aud_per_mwh:  r.price_aud_per_mwh === '' || r.price_aud_per_mwh == null
                              ? null
                              : Number(r.price_aud_per_mwh),
          kind:               r.kind || 'bundled',
          ...(r.notes ? { notes: r.notes } : {}),
        }));
      }

      if (Object.keys(changedFields).length === 0 && !filenameChanged) {
        // Nothing changed
        setEditMode(false);
        return;
      }

      let updatedContract = contract;

      // Save filename if changed
      if (filenameChanged) {
        const fnResponse = await axios.patch(
          `${API_BASE_URL}/api/contracts/${contractId}`,
          { original_filename: newFilename }
        );
        updatedContract = fnResponse.data;
      }

      // Save extraction fields if any changed. The backend's PATCH endpoint
      // accepts the legacy {fields} shape and translates each key into the
      // new field_path via LEGACY_FIELD_MAP — unknown keys are silently
      // dropped, so the user sees a partial save when prompt-extracted
      // descriptive fields aren't in the new schema yet.
      if (Object.keys(changedFields).length > 0) {
        const response = await axios.patch(
          `${API_BASE_URL}/api/contracts/${contractId}`,
          { fields: changedFields }
        );
        updatedContract = response.data;
      }

      setContract(adaptContract(updatedContract));
      setEditMode(false);
      setEditValues({});
      setSaveSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  if (error && !contract) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
          Back to Contracts
        </Button>
        <Alert severity="error" sx={{ border: '1px solid rgba(229, 56, 79, 0.2)' }}>{error}</Alert>
      </Box>
    );
  }

  if (!contract) return null;

  const labels =
    contract.extraction?.extraction_type === 'ppa' ? PPA_LABELS : GENERAL_LABELS;
  const extractedData = contract.extraction?.extracted_data || {};

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Box>
      {/* Header */}
      <Box
        className="animate-in"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' },
              transition: 'color 0.2s ease',
            }}
          >
            Back
          </Button>
          {editMode ? (
            <TextField
              value={editValues._filename || ''}
              onChange={(e) => handleFieldChange('_filename', e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                minWidth: 280,
                '& .MuiOutlinedInput-root': {
                  fontSize: '1.15rem',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  fontFamily: "'DM Sans', sans-serif",
                  '& fieldset': {
                    borderColor: 'rgba(217, 138, 43, 0.4)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '2px',
                  },
                },
              }}
            />
          ) : (
            <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 700, letterSpacing: '-0.02em' }}>
              {contract.original_filename}
            </Typography>
          )}
          <ContractStatusChip status={contract.status} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Edit / Save / Cancel buttons */}
          {contract.status === 'completed' && contract.extraction && (
            editMode ? (
              <>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} sx={{ color: '#F4F2EC' }} /> : <SaveIcon />}
                  onClick={saveAllFields}
                  disabled={saving}
                  sx={{
                    bgcolor: 'primary.main',
                    color: '#F4F2EC',
                    fontWeight: 600,
                    px: 2.5,
                    py: 0.7,
                    boxShadow: '0 2px 8px rgba(217, 138, 43, 0.20)',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      boxShadow: '0 4px 12px rgba(217, 138, 43, 0.30)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Save Changes
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  onClick={cancelEditMode}
                  disabled={saving}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.10)',
                    color: 'text.secondary',
                    fontWeight: 600,
                    px: 2,
                    py: 0.7,
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      bgcolor: 'rgba(255, 255, 255, 0.02)',
                    },
                  }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={enterEditMode}
                sx={{
                  borderColor: 'rgba(217, 138, 43, 0.30)',
                  color: 'primary.main',
                  fontWeight: 600,
                  px: 2.5,
                  py: 0.7,
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'rgba(217, 138, 43, 0.08)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Edit
              </Button>
            )
          )}

          {/* Actions Menu (three-dot) */}
          <Tooltip title="Actions">
            <IconButton
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'text.primary', bgcolor: 'rgba(255, 255, 255, 0.05)' },
                transition: 'all 0.2s ease',
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={menuOpen}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                minWidth: 200,
                border: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: '0 4px 20px rgba(255, 255, 255, 0.08)',
                borderRadius: '10px',
                mt: 0.5,
              },
            }}
          >
            <MenuItem
              onClick={handleToggleActive}
              disabled={activating}
              sx={{ py: 1.2, px: 2 }}
            >
              <ListItemIcon>
                <CheckCircleIcon
                  fontSize="small"
                  sx={{ color: contract.active ? '#22c55e' : 'text.secondary' }}
                />
              </ListItemIcon>
              <ListItemText
                primary={activating ? 'Updating…' : (contract.active ? 'Active — unmark' : 'Mark as active')}
                secondary={contract.active ? undefined : 'Make this the canonical contract for its asset'}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: '0.72rem' }}
              />
            </MenuItem>
            {(contract.status === 'completed' || contract.status === 'error') && (
              <MenuItem
                onClick={handleReprocess}
                disabled={reprocessing}
                sx={{ py: 1.2, px: 2 }}
              >
                <ListItemIcon>
                  <RefreshIcon
                    fontSize="small"
                    sx={{
                      color: 'primary.main',
                      ...(reprocessing && { animation: 'spin 1s linear infinite' }),
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={reprocessing ? 'Reprocessing...' : 'Reprocess Contract'}
                  primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                />
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                setDeleteDialogOpen(true);
              }}
              sx={{ py: 1.2, px: 2 }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText
                primary="Delete Contract"
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: 'error.main' }}
              />
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, border: '1px solid rgba(229, 56, 79, 0.2)' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Processing timeline — replaces the simple status pill with a
          step-by-step view of where the extraction pipeline is. Aurora-style:
          horizontal nodes, connector lines, per-step state + timestamp. */}
      <Box className="animate-in stagger-1" sx={{ mb: 2 }}>
        <ProcessingTimeline processing={contract.processing || { status: contract.status }} />
      </Box>

      {/* Metadata Card */}
      <Paper
        className="animate-in stagger-1"
        sx={{
          p: 3,
          mb: 3,
          bgcolor: 'background.paper',
          border: '1px solid', borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={metaLabelSx}>
              Contract Type
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              {contract.contract_type ? (
                <Chip
                  label={contract.contract_type}
                  size="small"
                  sx={{
                    bgcolor:
                      contract.contract_type === 'PPA'
                        ? 'rgba(217, 138, 43, 0.08)'
                        : 'rgba(59, 130, 246, 0.08)',
                    color:
                      contract.contract_type === 'PPA' ? '#D98A2B' : '#2d7dd2',
                    fontWeight: 600,
                    border:
                      contract.contract_type === 'PPA'
                        ? '1px solid rgba(217, 138, 43, 0.12)'
                        : '1px solid rgba(59, 130, 246, 0.18)',
                  }}
                />
              ) : (
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>{'\u2014'}</Typography>
              )}
              {contract.active && (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                  label="Active"
                  size="small"
                  sx={{
                    ml: 0.75, fontWeight: 600,
                    bgcolor: 'rgba(34, 197, 94, 0.10)', color: '#22c55e',
                    border: '1px solid rgba(34, 197, 94, 0.20)',
                    '& .MuiChip-icon': { color: '#22c55e' },
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Start Date */}
          <Box sx={{ minWidth: 150 }}>
            <Typography variant="caption" color="text.secondary" sx={metaLabelSx}>
              <CalendarTodayIcon sx={{ fontSize: 10, mr: 0.5, verticalAlign: 'middle' }} />
              Start Date
            </Typography>
            {editMode ? (
              <DatePicker
                value={toDateInputValue(editValues[contract.extraction?.extraction_type === 'ppa' ? 'start_date' : 'effective_date'])}
                onChange={(newVal) => handleFieldChange(
                  contract.extraction?.extraction_type === 'ppa' ? 'start_date' : 'effective_date',
                  newVal ? newVal.format('YYYY-MM-DD') : ''
                )}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      mt: 0.5,
                      maxWidth: 160,
                      '& .MuiOutlinedInput-root': {
                        height: 32,
                        fontSize: '0.82rem',
                        fontFamily: "'JetBrains Mono', monospace",
                        '& fieldset': { borderColor: 'rgba(217, 138, 43, 0.4)' },
                        '&:hover fieldset': { borderColor: 'primary.main' },
                        '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: '2px' },
                      },
                    },
                  },
                }}
              />
            ) : (
              <Typography
                variant="body1"
                sx={{
                  mt: 0.5,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.85rem',
                  color: contract.start_date ? 'primary.main' : 'text.secondary',
                  fontWeight: contract.start_date ? 600 : 400,
                }}
              >
                {formatContractDate(contract.start_date)}
              </Typography>
            )}
          </Box>

          {/* End Date */}
          <Box sx={{ minWidth: 150 }}>
            <Typography variant="caption" color="text.secondary" sx={metaLabelSx}>
              <CalendarTodayIcon sx={{ fontSize: 10, mr: 0.5, verticalAlign: 'middle' }} />
              End Date
            </Typography>
            {editMode ? (
              <DatePicker
                value={toDateInputValue(editValues[contract.extraction?.extraction_type === 'ppa' ? 'end_date' : 'expiry_date'])}
                onChange={(newVal) => handleFieldChange(
                  contract.extraction?.extraction_type === 'ppa' ? 'end_date' : 'expiry_date',
                  newVal ? newVal.format('YYYY-MM-DD') : ''
                )}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      mt: 0.5,
                      maxWidth: 160,
                      '& .MuiOutlinedInput-root': {
                        height: 32,
                        fontSize: '0.82rem',
                        fontFamily: "'JetBrains Mono', monospace",
                        '& fieldset': { borderColor: 'rgba(217, 138, 43, 0.4)' },
                        '&:hover fieldset': { borderColor: 'primary.main' },
                        '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: '2px' },
                      },
                    },
                  },
                }}
              />
            ) : (
              <Typography
                variant="body1"
                sx={{
                  mt: 0.5,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.85rem',
                  color: contract.end_date ? 'primary.main' : 'text.secondary',
                  fontWeight: contract.end_date ? 600 : 400,
                }}
              >
                {formatContractDate(contract.end_date)}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={metaLabelSx}>
              Uploaded
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mt: 0.5,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '0.85rem',
              }}
            >
              {contract.uploaded_at
                ? new Date(contract.uploaded_at).toLocaleDateString('en-AU', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })
                : '\u2014'}
            </Typography>
          </Box>

          {/* Covered assets \u2014 Name (DUID). Same data appears in the Term
              Sheet's Covered Assets section and the dashboard table's Covers
              column; one place to read each. */}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography variant="caption" color="text.secondary" sx={metaLabelSx}>
              Covered Assets
            </Typography>
            <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {(() => {
                const assets = contract.assets?.length > 0
                  ? contract.assets
                  : (contract.asset_ids || []).map(d => ({ asset_id: d, name: null }));
                if (!assets.length) {
                  return (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.82rem' }}>
                      Not matched yet \u2014 Reprocess to match against current portfolio assets
                    </Typography>
                  );
                }
                return assets.map(a => <AssetChip key={a.asset_id} asset={a} />);
              })()}
            </Box>
          </Box>
        </Box>

        {contract.error_message && (
          <Alert severity="error" sx={{ mt: 2, border: '1px solid rgba(229, 56, 79, 0.2)' }}>
            {contract.error_message}
          </Alert>
        )}

        {contract.extraction?.raw_classification?.reasoning && (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderTopColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={metaLabelSx}>
              Classification Reasoning
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 0.5, lineHeight: 1.6 }}
            >
              {contract.extraction.raw_classification.reasoning}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Tabs: Extracted Data | Document Preview */}
      {contract.status === 'completed' && (
        <Box className="animate-in stagger-2">
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{
              mb: 2,
              '& .MuiTab-root': {
                color: 'text.secondary',
                '&.Mui-selected': { color: 'primary.main' },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            <Tab
              icon={<DataObjectIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Extracted Data"
            />
            <Tab
              icon={<TaskAltIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={`Tasks${tasks.length > 0 ? ` (${tasks.length})` : ''}`}
            />
            <Tab
              icon={<DescriptionIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Document Preview"
            />
            {contract.contract_type === 'PPA' && (
              <Tab
                icon={<ShowChartIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Minimum Generation"
              />
            )}
          </Tabs>

          {/* Extracted Data Tab */}
          {activeTab === 0 && (
            <Paper
              sx={{
                bgcolor: 'background.paper',
                border: editMode ? '1px solid rgba(217, 138, 43, 0.20)' : '1px solid rgba(255, 255, 255, 0.05)',
                overflow: 'hidden',
                transition: 'border-color 0.3s ease',
              }}
            >
              {/* Edit mode indicator bar */}
              {editMode && (
                <Box
                  sx={{
                    px: 2.5,
                    py: 1,
                    bgcolor: 'rgba(217, 138, 43, 0.08)',
                    borderBottom: '1px solid rgba(217, 138, 43, 0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <EditIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography
                    variant="body2"
                    sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.8rem' }}
                  >
                    Editing mode
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', fontSize: '0.75rem', ml: 0.5 }}
                  >
                    — modify fields below and click Save Changes
                  </Typography>
                </Box>
              )}

              {/* Empty-state for read-only when nothing's been extracted yet. */}
              {!editMode && Object.entries(labels).every(([k]) => {
                const v = extractedData[k];
                return v === null || v === undefined || v === '';
              }) && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No fields extracted yet. Click <b>Edit</b> to add them manually,
                    or <b>Reprocess</b> to re-run the extraction pipeline.
                  </Typography>
                </Box>
              )}

              <Table size="small">
                <TableBody>
                  {(() => {
                    // Partition fields into populated vs empty so the
                    // "Not extracted" rows can be:
                    //   • hidden entirely in read-only mode (cleaner page)
                    //   • shown at the BOTTOM of the table in edit mode,
                    //     with a subtle "Not yet extracted" divider above them
                    const entries = Object.entries(labels);
                    const populated = entries.filter(([k]) => {
                      const v = extractedData[k];
                      return v !== null && v !== undefined && v !== '';
                    });
                    const empty = entries.filter(([k]) => {
                      const v = extractedData[k];
                      return v === null || v === undefined || v === '';
                    });
                    // Read-only: only populated. Edit mode: populated + divider + empty.
                    return editMode ? [...populated, ['__divider__', null], ...empty] : populated;
                  })().map(([key, label]) => {
                    // Synthetic divider row in edit mode separating populated
                    // from empty fields. Skip the normal row rendering.
                    if (key === '__divider__') {
                      return (
                        <TableRow key="__divider__" sx={{ bgcolor: 'transparent !important' }}>
                          <TableCell colSpan={2} sx={{
                            py: 1.5, borderBottom: 'none',
                            color: 'text.secondary',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                          }}>
                            Not yet extracted — fill in to add to the record
                          </TableCell>
                        </TableRow>
                      );
                    }
                    const value = extractedData[key];
                    const isMono = MONO_FIELDS.has(key);
                    const isMultiline = MULTILINE_FIELDS.has(key);
                    const displayValue = formatValue(value);
                    const hasValue = value !== null && value !== undefined;

                    return (
                      <TableRow
                        key={key}
                        sx={{
                          // Subtle zebra striping that adapts to mode.
                          // The old hard-coded #fafbfc rendered as near-white in
                          // dark mode (high-contrast bright stripes against the
                          // dark surface). Use a theme-aware overlay instead so
                          // the stripes are gentle in both modes.
                          '&:nth-of-type(odd)': {
                            bgcolor: (theme) => editMode
                              ? 'rgba(217, 138, 43, 0.015)'
                              : theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.025)'   // very faint over dark
                                : 'rgba(0, 0, 0, 0.020)',        // very faint over light
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            width: '28%',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            fontFamily: "'JetBrains Mono', monospace",
                            borderBottom: '1px solid', borderBottomColor: 'divider',
                            py: 1.5,
                            verticalAlign: 'top',
                          }}
                        >
                          {label}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: 'text.primary',
                            whiteSpace: 'pre-wrap',
                            borderBottom: '1px solid', borderBottomColor: 'divider',
                            py: editMode ? 1 : 1.5,
                            ...(isMono && !editMode && {
                              fontFamily: "'JetBrains Mono', monospace",
                              fontWeight: 500,
                            }),
                          }}
                        >
                          {editMode ? (
                            DATE_FIELDS.has(key) ? (
                              <DatePicker
                                value={toDateInputValue(editValues[key])}
                                onChange={(newVal) => handleFieldChange(
                                  key,
                                  newVal ? newVal.format('YYYY-MM-DD') : ''
                                )}
                                format="DD/MM/YYYY"
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    placeholder: `Select ${label.toLowerCase()}`,
                                    sx: {
                                      '& .MuiOutlinedInput-root': {
                                        fontSize: '0.875rem',
                                        fontFamily: "'JetBrains Mono', monospace",
                                        bgcolor: 'background.paper',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.10)' },
                                        '&:hover fieldset': { borderColor: 'rgba(217, 138, 43, 0.4)' },
                                        '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: '2px' },
                                      },
                                    },
                                  },
                                }}
                              />
                            ) : (
                            <TextField
                              value={editValues[key] || ''}
                              onChange={(e) => handleFieldChange(key, e.target.value)}
                              fullWidth
                              multiline={isMultiline}
                              minRows={isMultiline ? 2 : 1}
                              maxRows={isMultiline ? 6 : 1}
                              size="small"
                              placeholder={`Enter ${label.toLowerCase()}`}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  fontSize: '0.875rem',
                                  fontFamily: isMono
                                    ? "'JetBrains Mono', monospace"
                                    : "'DM Sans', sans-serif",
                                  bgcolor: 'background.paper',
                                  '& fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.10)',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: 'rgba(217, 138, 43, 0.4)',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                    borderWidth: '2px',
                                  },
                                },
                              }}
                            />
                            )
                          ) : (
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                color: hasValue ? 'text.primary' : 'text.secondary',
                                fontStyle: hasValue ? 'normal' : 'italic',
                                whiteSpace: 'pre-wrap',
                                ...(isMono && {
                                  fontFamily: "'JetBrains Mono', monospace",
                                  fontWeight: 500,
                                }),
                              }}
                            >
                              {hasValue ? displayValue : 'Not extracted'}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* ── Price Schedule sub-section ─────────────────────────────
                  Array-typed field that doesn't fit one-row-per-field in the
                  main grid. Hidden when empty in read-only; in edit mode every
                  price cell is a TextField that writes back to the array. */}
              {(() => {
                const sched = editMode
                  ? (editValues._price_schedule || [])
                  : (contract?.terms?.ppa?.price_schedule || []);
                if (!sched.length && !editMode) return null;
                return (
                  <Box sx={{ mt: 3 }}>
                    <Typography sx={{
                      px: 2.5, py: 1.5, fontSize: '0.7rem', fontWeight: 700,
                      color: 'text.secondary', textTransform: 'uppercase',
                      letterSpacing: '0.06em', borderTop: '1px solid', borderTopColor: 'divider',
                    }}>
                      Price Schedule ({sched.length} {sched.length === 1 ? 'period' : 'periods'})
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.7rem',
                                            textTransform: 'uppercase', letterSpacing: '0.04em',
                                            fontWeight: 600, width: '30%' }}>
                            Period
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.7rem',
                                            textTransform: 'uppercase', letterSpacing: '0.04em',
                                            fontWeight: 600, width: '15%' }}>
                            Kind
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.7rem',
                                            textTransform: 'uppercase', letterSpacing: '0.04em',
                                            fontWeight: 600 }} align="right">
                            A$ / MWh
                          </TableCell>
                        </TableRow>
                        {sched.map((row, i) => (
                          <TableRow key={i} sx={{
                            '&:nth-of-type(even)': {
                              bgcolor: (theme) => theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.020)',
                            },
                          }}>
                            <TableCell sx={{ py: editMode ? 1 : 1.5,
                                              fontFamily: "'JetBrains Mono', monospace",
                                              fontSize: '0.82rem' }}>
                              {editMode ? (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  <DatePicker
                                    value={toDateInputValue(row.period_start)}
                                    onChange={(v) => handlePriceScheduleChange(i, 'period_start', v ? v.format('YYYY-MM-DD') : '')}
                                    format="DD/MM/YYYY"
                                    slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                                  />
                                  <Box component="span" sx={{ color: 'text.disabled' }}>→</Box>
                                  <DatePicker
                                    value={toDateInputValue(row.period_end)}
                                    onChange={(v) => handlePriceScheduleChange(i, 'period_end', v ? v.format('YYYY-MM-DD') : '')}
                                    format="DD/MM/YYYY"
                                    slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                                  />
                                </Box>
                              ) : (
                                <>
                                  {row.period_start} <Box component="span" sx={{ color: 'text.disabled' }}>→</Box> {row.period_end}
                                </>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: editMode ? 1 : 1.5, fontSize: '0.82rem' }}>
                              {editMode ? (
                                <TextField
                                  value={row.kind || 'bundled'}
                                  onChange={(e) => handlePriceScheduleChange(i, 'kind', e.target.value)}
                                  size="small"
                                  fullWidth
                                />
                              ) : (
                                row.kind || 'bundled'
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ py: editMode ? 1 : 1.5,
                                                            fontFamily: "'JetBrains Mono', monospace",
                                                            fontSize: '0.85rem', fontWeight: 600 }}>
                              {editMode ? (
                                <TextField
                                  value={row.price_aud_per_mwh ?? ''}
                                  onChange={(e) => handlePriceScheduleChange(i, 'price_aud_per_mwh', e.target.value)}
                                  size="small"
                                  type="number"
                                  inputProps={{ step: '0.01', style: { textAlign: 'right', fontFamily: "'JetBrains Mono', monospace" } }}
                                  sx={{ width: 120 }}
                                />
                              ) : (
                                row.price_aud_per_mwh != null
                                  ? `A$${Number(row.price_aud_per_mwh).toFixed(2)}`
                                  : '—'
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                );
              })()}
            </Paper>
          )}

          {/* Tasks Tab */}
          {activeTab === 1 && (
            <Paper
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid', borderColor: 'divider',
                overflow: 'hidden',
              }}
            >
              {tasksLoading ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress size={24} sx={{ color: 'primary.main' }} />
                </Box>
              ) : tasks.length === 0 ? (
                <Box sx={{ p: 5, textAlign: 'center' }}>
                  <TaskAltIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 1.5 }} />
                  <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    No tasks extracted from this contract.
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, opacity: 0.7 }}>
                    Tasks are automatically generated during document processing.
                  </Typography>
                </Box>
              ) : (
                <>
                {/* Re-run team routing over the existing tasks. Cheap (no OCR,
                    no re-extraction) and safe — it never touches a task
                    someone has assigned or deliberately unassigned. */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                           px: 2, py: 1.25, borderBottom: '1px solid', borderBottomColor: 'divider' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {unassignedCount > 0
                      ? `${unassignedCount} of ${tasks.length} not assigned to a team`
                      : `All ${tasks.length} tasks assigned`}
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleAutoAssign}
                    disabled={autoAssigning || teams.length === 0}
                    startIcon={autoAssigning
                      ? <CircularProgress size={13} />
                      : <AutoAwesomeIcon sx={{ fontSize: 15 }} />}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    {teams.length === 0 ? 'No teams yet' : 'Auto-assign teams'}
                  </Button>
                </Box>
                <Table size="small">
                  <TableBody>
                    {/* Header Row */}
                    <TableRow sx={{ bgcolor: 'rgba(217, 138, 43, 0.08)' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', width: 40 }} />
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Task</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', width: 110 }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', width: 90 }}>Priority</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', width: 170 }}>Assigned to</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', width: 250 }}>Recurrence / due</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', width: 50 }} align="right" />
                    </TableRow>
                    {tasks.map((task) => {
                      const priorityStyle = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
                      const catColor = CATEGORY_COLORS[task.category] || CATEGORY_COLORS.other;
                      const isCompleted = task.status === 'completed';

                      return (
                        <TableRow
                          key={task.id}
                          sx={{
                            opacity: isCompleted ? 0.55 : 1,
                            transition: 'all 0.2s ease',
                            '&:hover': { bgcolor: 'rgba(217, 138, 43, 0.02)' },
                          }}
                        >
                          {/* Checkbox */}
                          <TableCell sx={{ borderBottom: '1px solid', borderBottomColor: 'divider', py: 1.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleTaskStatus(task.id)}
                              sx={{ color: isCompleted ? 'primary.main' : 'text.secondary', p: 0.5 }}
                            >
                              {isCompleted ? <CheckBoxIcon sx={{ fontSize: 20 }} /> : <CheckBoxOutlineBlankIcon sx={{ fontSize: 20 }} />}
                            </IconButton>
                          </TableCell>
                          {/* Title & Description — editable inline when contract is in edit mode */}
                          <TableCell sx={{ borderBottom: '1px solid', borderBottomColor: 'divider', py: 1.5 }}>
                            {editMode ? (
                              <Stack spacing={0.75}>
                                <TextField
                                  defaultValue={task.title}
                                  size="small"
                                  fullWidth
                                  onBlur={(e) => {
                                    const v = e.target.value.trim();
                                    if (v && v !== task.title) handleEditTaskContent(task.id, { title: v });
                                  }}
                                  sx={{ '& input': { fontWeight: 600, fontSize: '0.88rem' } }}
                                />
                                <TextField
                                  defaultValue={task.description || ''}
                                  size="small"
                                  fullWidth
                                  multiline
                                  minRows={1}
                                  maxRows={4}
                                  placeholder="Description (optional)"
                                  onBlur={(e) => {
                                    const v = e.target.value;
                                    if (v !== (task.description || '')) handleEditTaskContent(task.id, { description: v });
                                  }}
                                  sx={{ '& textarea': { fontSize: '0.78rem' } }}
                                />
                              </Stack>
                            ) : (
                              <>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    textDecoration: isCompleted ? 'line-through' : 'none',
                                    color: isCompleted ? 'text.secondary' : 'text.primary',
                                  }}
                                >
                                  {task.title}
                                  {task.is_recurring && (
                                    <Chip
                                      label={task.recurrence_pattern || 'recurring'}
                                      size="small"
                                      sx={{
                                        ml: 1, height: 18, fontSize: '0.62rem',
                                        bgcolor: 'rgba(217, 138, 43, 0.08)',
                                        color: 'primary.main', fontWeight: 600,
                                      }}
                                    />
                                  )}
                                </Typography>
                                {task.description && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary', display: 'block',
                                      mt: 0.3, lineHeight: 1.4, maxWidth: 500,
                                    }}
                                  >
                                    {task.description}
                                  </Typography>
                                )}
                              </>
                            )}
                          </TableCell>
                          {/* Category */}
                          <TableCell sx={{ borderBottom: '1px solid', borderBottomColor: 'divider', py: 1.5 }}>
                            <Chip
                              label={task.category}
                              size="small"
                              sx={{
                                bgcolor: `${catColor}12`,
                                color: catColor,
                                fontWeight: 600,
                                fontSize: '0.68rem',
                                border: `1px solid ${catColor}25`,
                                textTransform: 'capitalize',
                              }}
                            />
                          </TableCell>
                          {/* Priority */}
                          <TableCell sx={{ borderBottom: '1px solid', borderBottomColor: 'divider', py: 1.5 }}>
                            <Chip
                              icon={<FlagIcon sx={{ fontSize: 12 }} />}
                              label={task.priority}
                              size="small"
                              sx={{
                                bgcolor: priorityStyle.bg,
                                color: priorityStyle.color,
                                border: priorityStyle.border,
                                fontWeight: 600,
                                fontSize: '0.68rem',
                                textTransform: 'capitalize',
                                '& .MuiChip-icon': { color: priorityStyle.color },
                              }}
                            />
                          </TableCell>
                          {/* Assigned to — suggested during document processing,
                              correctable here. See TaskTeamPicker. */}
                          <TableCell sx={{ borderBottom: '1px solid', borderBottomColor: 'divider', py: 1.5 }}>
                            <TaskTeamPicker
                              contractId={contractId}
                              task={task}
                              teams={teams}
                              onChanged={handleTeamChanged}
                            />
                          </TableCell>
                          {/* Recurring obligations get a recurrence rule; one-off
                              events keep a plain date. A single date was the wrong
                              control for a repeating task — see TaskRecurrence. */}
                          <TableCell sx={{ borderBottom: '1px solid', borderBottomColor: 'divider', py: 1.5 }}>
                            {task._kind === 'obligation' ? (
                              <TaskRecurrence
                                contractId={contractId}
                                task={task}
                                onChanged={handleRecurrenceChanged}
                              />
                            ) : (
                            <DatePicker
                              value={task.due_date ? dayjs(task.due_date) : null}
                              onChange={(newVal) => handleAssignTaskDate(task.id, newVal)}
                              format="DD/MM/YYYY"
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  placeholder: task.due_date ? '' : 'Assign date',
                                  sx: {
                                    maxWidth: 170,
                                    '& .MuiOutlinedInput-root': {
                                      height: 32,
                                      fontSize: '0.78rem',
                                      fontFamily: "'JetBrains Mono', monospace",
                                      ...(task.due_date ? {} : {
                                        '& fieldset': { borderColor: 'rgba(230, 126, 34, 0.4)', borderStyle: 'dashed' },
                                        '&:hover fieldset': { borderColor: '#e67e22' },
                                      }),
                                    },
                                  },
                                },
                              }}
                            />
                            )}
                          </TableCell>
                          {/* Delete */}
                          <TableCell align="right" sx={{ borderBottom: '1px solid', borderBottomColor: 'divider', py: 1.5 }}>
                            <Tooltip title="Delete task">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteTask(task.id)}
                                sx={{
                                  color: 'text.secondary',
                                  opacity: 0.5,
                                  '&:hover': { color: 'error.main', opacity: 1 },
                                  transition: 'all 0.2s ease',
                                  p: 0.5,
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </>
              )}
            </Paper>
          )}

          {/* Document Preview Tab */}
          {activeTab === 2 && (
            <Paper
              sx={{
                p: 3,
                bgcolor: 'background.paper',
                border: '1px solid', borderColor: 'divider',
              }}
            >
              {markdownLoading ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress size={24} sx={{ color: 'primary.main' }} />
                </Box>
              ) : (
                <Box
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '0.82rem',
                    color: 'text.primary',
                    lineHeight: 1.8,
                    m: 0,
                    maxHeight: '70vh',
                    overflow: 'auto',
                    borderLeft: '2px solid rgba(217, 138, 43, 0.30)',
                    pl: 2.5,
                  }}
                >
                  {markdown || 'No markdown content available.'}
                </Box>
              )}
            </Paper>
          )}

          {/* PPA Minimum Generation Tab (only present for PPA contracts) */}
          {contract.contract_type === 'PPA' && activeTab === 3 && (
            <Box className="animate-in">
              <PpaMinGeneration contractId={contractId} />
            </Box>
          )}
        </Box>
      )}

      {/* Processing state */}
      {isProcessing(contract.status) && (
        <Paper
          className="animate-in stagger-2"
          sx={{
            p: 5,
            textAlign: 'center',
            bgcolor: 'background.paper',
            border: '1px solid', borderColor: 'divider',
          }}
        >
          <CircularProgress sx={{ color: 'primary.main', mb: 2 }} />
          <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
            Processing contract...
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, opacity: 0.8 }}>
            {contract.status === 'processing_ocr' &&
              'Converting PDF to text using Document AI...'}
            {contract.status === 'processing_classification' &&
              'Classifying contract type with AI...'}
            {contract.status === 'processing_extraction' &&
              'Extracting structured data with AI...'}
            {contract.status === 'extracting_tasks' &&
              'Extracting tasks and deadlines with AI...'}
            {contract.status === 'uploading' && 'Uploading to cloud storage...'}
          </Typography>
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Contract</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.secondary' }}>
            Are you sure you want to delete "{contract.original_filename}"? This
            will remove the contract, its extracted data, and all associated
            files. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Success Snackbar */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={2000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSaveSuccess(false)}
          severity="success"
          variant="filled"
          sx={{
            bgcolor: 'primary.main',
            '& .MuiAlert-icon': { color: '#F4F2EC' },
          }}
        >
          Changes saved successfully
        </Alert>
      </Snackbar>
    </Box>
    </LocalizationProvider>
  );
};

export default ContractDetail;
